import { api } from './api';

// Outbound email service.
// Aligned to app/api/v1/routers/email_router.py (prefix /api/v1/email).
// Uses the shared local `api` instance (src/services/api.ts).

const BASE = '/api/v1/email';

export type EmailStatus = 'QUEUED' | 'SENDING' | 'SENT' | 'FAILED' | 'CANCELLED';

// Note: there is deliberately no DELIVERED/READ status. SMTP acknowledges
// submission, not delivery — the backend will never report more than SENT.
export const EMAIL_STATUSES: EmailStatus[] = [
  'QUEUED',
  'SENDING',
  'SENT',
  'FAILED',
  'CANCELLED',
];

export interface EmailAttachmentIn {
  filename?: string;
  mimeType?: string;
  /** Base64 bytes. Mutually exclusive with fileId. */
  contentBase64?: string;
  /** Existing file_metadata._id. Mutually exclusive with contentBase64. */
  fileId?: string;
  sizeBytes?: number;
  /** Embed in the HTML body; must be an image. */
  isInline?: boolean;
  /** cid reference for inline images, e.g. 'logo' for <img src="cid:logo">. */
  contentId?: string;
}

export interface EmailAttachmentOut {
  filename?: string;
  mimeType?: string;
  isInline: boolean;
  source: 'inline' | 'fileId';
}

export interface OutboxEntry {
  id: string;
  source: string;
  status: EmailStatus;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  attempts: number;
  lastError?: string | null;
  provider?: string | null;
  providerMessageId?: string | null;
  attachments: EmailAttachmentOut[];
  /** Returned by GET /outbox/{id} only — the list projection strips both. */
  bodyHtml?: string | null;
  bodyText?: string | null;
  notificationId?: string | null;
  scheduledAt?: string | null;
  nextAttemptAt?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SendEmailPayload {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  replyTo?: string;
  attachments?: EmailAttachmentIn[];
  scheduledAt?: string | null;
}

export interface OutboxListParams {
  status?: EmailStatus | '';
  recipient?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface OutboxListResponse {
  data: OutboxEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Backend envelope: { success, code, message, data, errors }. */
interface Envelope<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export const emailService = {
  /**
   * Queue a message. Returns 202 — accepted, not delivered.
   * Pass `idempotencyKey` when retrying so a network blip can't send twice.
   */
  async send(payload: SendEmailPayload, idempotencyKey?: string): Promise<OutboxEntry> {
    const response = await api.post<Envelope<OutboxEntry>>(`${BASE}/send`, payload, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
    return response.data.data;
  },

  async listOutbox(params: OutboxListParams = {}): Promise<OutboxListResponse> {
    const response = await api.get<Envelope<OutboxListResponse>>(`${BASE}/outbox`, {
      params: {
        status: params.status || undefined,
        recipient: params.recipient || undefined,
        search: params.search || undefined,
        dateFrom: params.dateFrom || undefined,
        dateTo: params.dateTo || undefined,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    });
    return response.data.data;
  },

  async getEntry(id: string): Promise<OutboxEntry> {
    const response = await api.get<Envelope<OutboxEntry>>(`${BASE}/outbox/${id}`);
    return response.data.data;
  },

  /** Queues a fresh copy; the original row is kept for audit. */
  async resend(id: string): Promise<OutboxEntry> {
    const response = await api.post<Envelope<OutboxEntry>>(`${BASE}/outbox/${id}/resend`);
    return response.data.data;
  },

  /** Only works while still QUEUED — 400 once the worker has picked it up. */
  async cancel(id: string): Promise<boolean> {
    const response = await api.post<Envelope<{ cancelled: boolean }>>(
      `${BASE}/outbox/${id}/cancel`,
    );
    return response.data.data.cancelled;
  },

  /** Render a template without sending. HTML comes back sanitized. */
  async preview(body: {
    subject?: string;
    bodyHtml: string;
    payload?: Record<string, unknown>;
    wrapInShell?: boolean;
  }): Promise<{ subject: string; html: string }> {
    const response = await api.post<Envelope<{ subject: string; html: string }>>(
      `${BASE}/preview`,
      { wrapInShell: true, ...body },
    );
    return response.data.data;
  },

  /** Bypasses the queue so SMTP errors surface immediately. */
  async testSend(to: string, subject?: string, bodyHtml?: string) {
    const response = await api.post<Envelope<unknown>>(`${BASE}/test-send`, {
      to,
      ...(subject ? { subject } : {}),
      ...(bodyHtml ? { bodyHtml } : {}),
    });
    return response.data.data;
  },
};

export interface UploadedAttachment {
  fileId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  /** SAS url — usable directly in a push `image` field. */
  url?: string | null;
}

/**
 * Upload a file for use as an attachment.
 *
 * Uses /api/v1/email/attachments rather than the generic /api/v1/files/upload,
 * which requires `district` and `module` headers validated against master data
 * that a notification template does not have.
 */
export async function uploadAttachment(file: File): Promise<UploadedAttachment> {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post<Envelope<UploadedAttachment>>(
    `${BASE}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data.data;
}

/** Read a File into the base64 shape the send API expects. */
export function fileToAttachment(file: File): Promise<EmailAttachmentIn> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      // result is a data: URL — strip the "data:<mime>;base64," prefix.
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64: comma >= 0 ? result.slice(comma + 1) : result,
      });
    };
    reader.readAsDataURL(file);
  });
}

export default emailService;
